// Estimate pricing math — assembly/item-driven cost + the sequential
// commercial stack, originally reverse-engineered from the Strive AZ
// workbook's live formulas (03 SCOPE TAKEOFF!AA6 and 13 DESCOPE CONTROL)
// for Electrical, then confirmed generic and renamed off "electricalPricing"
// (2026-09-04) once a second trade needed the same math. Pure functions
// only, no DB access, mirroring takeoffMath.js's convention: the DB stores
// raw inputs (Assembly/Item reference + Quantity/RouteLF) and this module
// computes Material $/Labor $/Direct Cost/Sell Value live — nothing here is
// persisted. EstimateCostItems and EstimateCostAssemblies (db/30) are
// structurally identical "priceable entities" here (UOM/MaterialCostPerUnit/
// LaborHrPerUnit/VendorSubCostPerUnit) — either can be priced the same way,
// by any trade that adds rows into those tables via the shared Trade column.

import { tryEvaluateFormula } from "./formulaEngine";

// Sequential commercial stack: each layer compounds on the running subtotal,
// not a flat percentage of the bare direct cost. Verified directly against
// the workbook's cell formula:
//   (1+PmGc) x (1+Insurance+Escalation+Contingency) x (1+Overhead) x (1+Profit)
export function computeSellMultiplier(settings) {
  return (
    (1 + settings.PmFieldGcPct) *
    (1 + settings.InsuranceBondPct + settings.EscalationPct + settings.ContingencyPct) *
    (1 + settings.OverheadPct) *
    (1 + settings.ProfitPct)
  );
}

// Per-UNIT sell price (not scaled by quantity — see computeLineCost, which
// multiplies this by qty). An entity carrying SalesBurdenPct/MarkupPct (the
// "Project Estimate - CS" template's own model) opts out of the whole-
// estimate sequential stack below and prices with its own flat burden+
// markup instead, then — same as the template — rounds to a psychological-
// pricing increment: PriceRoundIncrement/PriceRoundOffset are per-row,
// fully editable fields (not a fixed rule) per direct feedback ("we are in
// a competitive market... everything need to be editable"), reverse-
// matched to the template's own live cell formulas, which vary by row
// (=CEILING(Cost*(1+Burden+Markup),1)+0.95 for panels, ...,5) for doors/
// hardware, ...,0.05) for coving/trim — see db/30's header comment for the
// full derivation). NULL/0 increment = no rounding, raw math only.
// Confirmed exact: Wall Panel System, cost 16.01/sf (the sheet's own
// 16.0125/sf rounded to the nearest cent — this app prices in dollars and
// cents, not fractions of a cent), burden 1.5%, markup 20%, round
// increment 1, round offset 0.95 -> CEILING(16.01*1.215, 1) + 0.95 = $20.95,
// matching the worksheet's own Unit Price column to the cent.
// Both SalesBurdenPct/MarkupPct NULL (every pre-existing row, still priced
// through EstimateCostSettings' stack) falls back unchanged.
export function computeUnitSellPrice(unitCost, entity, settings) {
  if (entity && (entity.SalesBurdenPct != null || entity.MarkupPct != null)) {
    const raw = unitCost * (1 + (Number(entity.SalesBurdenPct) || 0) / 100 + (Number(entity.MarkupPct) || 0) / 100);
    const increment = Number(entity.PriceRoundIncrement) || 0;
    if (increment > 0) {
      const offset = Number(entity.PriceRoundOffset) || 0;
      return Math.ceil(raw / increment) * increment + offset;
    }
    return raw;
  }
  return unitCost * computeSellMultiplier(settings);
}

// Billable quantity for one row against its assembly/item. When the
// assembly's QuantityFormulaID resolves in formulasById (db/29's
// EstimateCostFormulas — editable without a code deploy), that formula computes the
// quantity instead of the flat rule below. Otherwise: an EA-priced entity (a
// panel, a package) prices as one unit; a row with a RouteLF (circuits,
// equipment connections) prices against it; a row with only a Qty (a direct
// Estimate Line Item — see EstimateCostLineItems) prices against that.
export function quantityFor(row, assembly, formulasById = new Map()) {
  if (!assembly) return 0;

  const formula = formulasById.get(assembly.QuantityFormulaID);
  if (formula) {
    const viaFormula = tryEvaluateFormula(formula.Expression, { routeLF: row.RouteLF ?? 0, qty: row.Qty ?? 1 });
    if (viaFormula !== null) return viaFormula;
  }

  if (assembly.UOM === "EA") return row.Qty ?? 1;
  if (row.RouteLF !== undefined) return row.RouteLF ?? 0;
  return row.Qty ?? 1;
}

// Cost breakdown for one priced row (a circuit, an equipment connection, a
// panel, or a direct line item) against its assembly/item + labor factor +
// this revision's settings.
//
// Carried price (P3-C, src/utils/carriedPricing.js): a direct Estimate Line
// Item may carry a unit cost from evidence (a project quote line, or a manual
// entry) — row.CostSource !== 'catalog' with row.CarriedUnitCost set. The line
// then prices at qty × carried cost; the material/labor/vendor split is
// scaled proportionally from the entity's own split so category rollups
// still reconcile (labor HOURS stay physical — never scaled by price). An
// entity with no catalog cost puts a quoted carried cost under vendor/sub and
// a manual one under material. Rows without those fields behave exactly as
// before.
export function computeLineCost(row, assembly, laborFactor, settings, formulasById = new Map()) {
  if (!assembly) return { qty: 0, materialCost: 0, laborHours: 0, laborCost: 0, vendorCost: 0, directCost: 0, sellValue: 0, unitCost: 0, carried: false, costSource: "catalog" };

  const qty = quantityFor(row, assembly, formulasById);
  const factorMult = laborFactor?.Multiplier ?? 1;
  const laborHours = qty * (assembly.LaborHrPerUnit || 0) * factorMult;
  const catalogUnit = (assembly.MaterialCostPerUnit || 0) + (assembly.LaborHrPerUnit || 0) * factorMult * (settings.LaborRate || 0) + (assembly.VendorSubCostPerUnit || 0);

  const carriedRaw = row?.CarriedUnitCost;
  const carriedNum = carriedRaw === null || carriedRaw === undefined || carriedRaw === "" ? null : Number(carriedRaw);
  const carried = !!row?.CostSource && row.CostSource !== "catalog" && Number.isFinite(carriedNum);
  const unitCost = carried ? carriedNum : catalogUnit;

  let materialCost, laborCost, vendorCost;
  if (!carried) {
    materialCost = qty * (assembly.MaterialCostPerUnit || 0);
    laborCost = laborHours * (settings.LaborRate || 0);
    vendorCost = qty * (assembly.VendorSubCostPerUnit || 0);
  } else if (catalogUnit > 0) {
    const scale = unitCost / catalogUnit;
    materialCost = qty * (assembly.MaterialCostPerUnit || 0) * scale;
    laborCost = laborHours * (settings.LaborRate || 0) * scale;
    vendorCost = qty * (assembly.VendorSubCostPerUnit || 0) * scale;
  } else {
    materialCost = row.CostSource === "quote" ? 0 : qty * unitCost;
    laborCost = 0;
    vendorCost = row.CostSource === "quote" ? qty * unitCost : 0;
  }
  const directCost = materialCost + laborCost + vendorCost;
  const sellValue = qty * computeUnitSellPrice(unitCost, assembly, settings);

  return { qty, materialCost, laborHours, laborCost, vendorCost, directCost, sellValue, unitCost, carried, costSource: carried ? row.CostSource : "catalog" };
}

const emptyGroup = () => ({ materialCost: 0, laborHours: 0, laborCost: 0, vendorCost: 0, directCost: 0, sellValue: 0, count: 0 });

function addLine(group, line) {
  group.materialCost += line.materialCost;
  group.laborHours += line.laborHours;
  group.laborCost += line.laborCost;
  group.vendorCost += line.vendorCost;
  group.directCost += line.directCost;
  group.sellValue += line.sellValue;
  group.count += 1;
}

// Rollup by group key — LoadClass for circuits, 'equipment' for connections,
// 'distribution' for panels, and (for direct Estimate Line Items) the
// resolved EstimateCostCategories name of whichever Item/Assembly was added. Same
// cascading math as computeLineCost, just aggregated. Returns
// { byGroup: { [key]: totals }, grandTotal: totals }.
export function computeRollup(
  { circuits = [], connections = [], panels = [], lineItems = [] },
  assembliesById, laborFactorsById, settings,
  { itemsById = new Map(), categoriesById = new Map(), formulasById = new Map() } = {}
) {
  const byGroup = {};
  const grandTotal = emptyGroup();

  const record = (row, entity, groupKey, laborFactor) => {
    if (!entity) return;
    const line = computeLineCost(row, entity, laborFactor, settings, formulasById);
    (byGroup[groupKey] ||= emptyGroup());
    addLine(byGroup[groupKey], line);
    addLine(grandTotal, line);
  };

  const roll = (row, assemblyId, groupKey, laborFactorId) => {
    const assembly = assembliesById.get(assemblyId);
    const laborFactor = laborFactorId ? laborFactorsById.get(laborFactorId) : laborFactorsById.get(assembly?.DefaultLaborFactorID);
    record(row, assembly, groupKey, laborFactor);
  };

  for (const c of circuits) roll(c, c.AssemblyID, c.LoadClass || "unassigned");
  for (const c of connections) roll(c, c.AssemblyID, "equipment");
  for (const p of panels) roll({ Qty: 1 }, p.AssemblyID, "distribution");

  for (const li of lineItems) {
    const entity = li.Kind === "item" ? itemsById.get(li.ItemID) : assembliesById.get(li.AssemblyID);
    if (!entity) continue;
    const laborFactor = li.LaborFactorID ? laborFactorsById.get(li.LaborFactorID) : laborFactorsById.get(entity.DefaultLaborFactorID);
    const groupKey = categoriesById.get(entity.CategoryID)?.Name || "Uncategorized";
    record({ Qty: li.Qty }, entity, groupKey, laborFactor);
  }

  return { byGroup, grandTotal };
}
